import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon } from "@sagot/ui";

interface StepTestProps {
  workspaceSlug: string | undefined;
  assistantName: string;
  /** Template starter questions shown as things to try. */
  sampleQuestions: readonly string[];
  onBack: () => void;
  onNext: () => void;
}

const CHECKS = ["checkRequirements", "checkLanguage", "checkAction", "checkGap"] as const;

/**
 * Step 4: the live test chat needs an agent + the chat API (P5); until then the card hands off to
 * the Playground, and the checklist explains what to verify there.
 */
export function StepTest({ workspaceSlug, assistantName, sampleQuestions, onBack, onNext }: StepTestProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("onboarding.test.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("onboarding.test.subtitle")}</p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
        <section className="card flex min-h-[420px] flex-col overflow-hidden lg:h-[520px]" aria-labelledby="test-chat-title">
          <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <span className="inline-flex size-8 items-center justify-center rounded-pill bg-ink-950 text-brand-500 dark:bg-brand-500 dark:text-ink-950">
              <Icon name="sparkles" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="test-chat-title" className="text-sm font-bold">
                {assistantName}
              </h2>
              <div className="flex items-center gap-1 text-[11px] text-success">
                <span className="dot" />
                {t("onboarding.test.testMode")}
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <span className="inline-flex size-12 items-center justify-center rounded-pill bg-primary-soft text-primary-ink">
              <Icon name="messages-square" className="size-6" />
            </span>
            <div className="text-sm font-bold">{t("onboarding.test.tryTitle")}</div>
            <p className="max-w-sm text-sm text-fg-muted">{t("onboarding.test.tryBody")}</p>
            {workspaceSlug ? (
              <Button asChild variant="secondary">
                <AppLink href={consoleRoutes.agents(workspaceSlug)}>
                  <Icon name="external-link" />
                  {t("onboarding.test.openPlayground")}
                </AppLink>
              </Button>
            ) : null}
          </div>
        </section>
        <aside className="space-y-4">
          <section className="card" aria-labelledby="checks-title">
            <div className="card-header">
              <h2 id="checks-title" className="card-title">
                {t("onboarding.test.checksTitle")}
              </h2>
            </div>
            <ul className="card-body space-y-3 text-[13px]">
              {CHECKS.map((key) => (
                <li key={key} className="flex items-start gap-2.5 text-fg-muted">
                  <Icon name="circle" className="mt-0.5 size-4" />
                  <span>{t(`onboarding.test.${key}`)}</span>
                </li>
              ))}
            </ul>
          </section>
          {sampleQuestions.length ? (
            <section className="card" aria-labelledby="try-questions-title">
              <div className="card-header">
                <h2 id="try-questions-title" className="card-title">
                  {t("onboarding.test.tryQuestions")}
                </h2>
              </div>
              <ul className="card-body space-y-2 text-[13px] text-fg-muted">
                {sampleQuestions.map((question) => (
                  <li key={question} className="flex items-start gap-2">
                    <Icon name="message-circle" className="mt-0.5 size-4 text-primary-ink" />
                    <span>{question}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <div className="rounded-lg border border-line bg-surface p-4 text-xs text-fg-muted">
            <div className="flex items-start gap-2">
              <Icon name="info" className="mt-0.5 size-4 text-primary-ink" />
              <span>
                {t("onboarding.test.noteLead")} <strong className="text-fg">{t("onboarding.test.notePlayground")}</strong> {t("onboarding.test.noteTail")}
              </span>
            </div>
          </div>
        </aside>
      </div>
      <div className="flex items-center justify-between border-t border-line pt-5">
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("onboarding.back")}
        </Button>
        <Button onClick={onNext}>
          {t("onboarding.test.next")}
          <Icon name="arrow-right" />
        </Button>
      </div>
    </div>
  );
}
